import styled from '@emotion/styled';
import { Space, Button, Collapse } from 'antd';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import { CopyOutlined } from '@ant-design/icons';
import { useCopyText } from 'hooks/useCopyText';
import { useState } from 'react';
import { useMemoizedFn, useMount } from 'ahooks';
import { unstable_batchedUpdates } from 'react-dom';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import SiderImageSlide from 'views/Conversation/Sider/SiderImageSlide';
import { get, map } from 'lodash-es';

interface ReturnRecordProps {
  type: 'mini' | 'panel';
  returnSn: string;
  data: Conversation.Trade.ReturnHistory[];
}

interface ReturnRecordTranslateProps {
  translateResult?: Record<string, string>;
  showTransalte: boolean;
}

interface ReturnRecordContentProps extends ReturnRecordTranslateProps {
  data: Conversation.Trade.ReturnHistory;
  index: number;
}

const renderDisplayText = (
  key: string,
  index: number,
  original: string,
  toggle: boolean,
  translateSource?: Record<string, string>,
) => {
  return toggle ? translateSource?.[`${key}.${index}`] : original;
};

const ReturnRecordContentHeader = (props: ReturnRecordContentProps) => {
  const { data, showTransalte, translateResult, index } = props;
  return (
    <div>
      <Space>
        <Dot />
        <strong>
          {renderDisplayText(
            'description',
            index,
            data.description,
            showTransalte,
            translateResult,
          )}
        </strong>
      </Space>
      <Time>{dayjs(toNumber(data.updateTime)).format('YYYY-MM-DD HH:mm:ss')}</Time>
    </div>
  );
};

const ReturnRecordContent = (props: ReturnRecordProps & ReturnRecordTranslateProps) => {
  const { data, returnSn, showTransalte, translateResult } = props;
  const { t } = useTranslate();
  const copyText = useCopyText();

  return (
    <Collapse ghost defaultActiveKey="0">
      {data.map((item, index) => {
        const { reason, buyerMessage, buyerImage, buyerVideo } = item;
        const length = get(buyerImage, 'length', 0) + get(buyerVideo, 'length', 0);
        const video = map(buyerVideo, 'videoUrl');

        return (
          <CollapsePanel
            key={index}
            header={
              <ReturnRecordContentHeader
                index={index}
                data={item}
                translateResult={translateResult}
                showTransalte={showTransalte}
              />
            }
          >
            <Space direction="vertical" size={12}>
              {index === 0 && (
                <div>
                  <Label>{t('scene.orderWorkspace.returnRefundTab.detail.field.refundId')}</Label>
                  <div>
                    {returnSn}
                    <CopyOutlined onClick={() => copyText(returnSn)} />
                  </div>
                </div>
              )}
              {reason && (
                <div>
                  <Label>
                    {t('scene.orderWorkspace.returnRefundTab.detail.section.afterSalesReason')}
                  </Label>
                  <div>
                    {renderDisplayText('reason', index, reason, showTransalte, translateResult)}
                  </div>
                </div>
              )}
              {buyerMessage && (
                <div>
                  <Label>
                    {t('scene.orderWorkspace.returnRefundTab.detail.section.otherReason')}
                  </Label>
                  <div>
                    {renderDisplayText(
                      'buyerMessage',
                      index,
                      buyerMessage,
                      showTransalte,
                      translateResult,
                    )}
                  </div>
                  <PreviewModalProvider>
                    <SiderImageSlide
                      videos={video}
                      images={buyerImage}
                      count={length}
                      style={{ width: `${85 * length - 5}px` }}
                      index={0}
                      width={640}
                      offsetPercent={2 / length}
                    />
                  </PreviewModalProvider>
                </div>
              )}
            </Space>
          </CollapsePanel>
        );
      })}
    </Collapse>
  );
};
export const ReturnRecord = (props: ReturnRecordProps) => {
  const { type, data } = props;
  const { t } = useTranslate();

  const [translateResult, setTranslateResult] = useState<Record<string, string> | undefined>();
  const [translateLoading, setTranslateLoading] = useState(false);
  // 翻译切换
  const [translateToggle, setTranslateToggle] = useState(false);

  const systemLanguage = useSystemLanguageValue();

  const handleTranslate = useMemoizedFn(async () => {
    if (translateResult) {
      setTranslateToggle(!translateToggle);
    } else {
      // 准备批量翻译的文本  [key, content]
      const originalText = data.reduce((prev, current, index) => {
        const { description, buyerMessage, reason } = current;
        if (description) {
          prev.push([`description.${index}`, description]);
        }
        if (buyerMessage) {
          prev.push([`buyerMessage.${index}`, buyerMessage]);
        }
        if (reason) {
          prev.push([`reason.${index}`, reason]);
        }
        return prev;
      }, [] as Array<[string, string]>);

      try {
        setTranslateLoading(true);
        const waitToTrans = originalText.map(([, text]) => text);
        const result = await overseaTranslate(waitToTrans, systemLanguage, 'drawer');
        unstable_batchedUpdates(() => {
          setTranslateResult(
            result.reduce((prev, current, index) => {
              const [key] = originalText[index];
              return {
                ...prev,
                [key]: current,
              };
            }, {}),
          );
          setTranslateToggle(!translateToggle);
        });
      } catch (err) {
      } finally {
        setTranslateLoading(false);
      }
    }
  });

  const translateBtn = (
    <Button type="link" size="small" loading={translateLoading} onClick={handleTranslate}>
      {t(translateToggle ? 'trade.查看原文' : 'trade.查看翻译')}
    </Button>
  );

  useMount(() => {
    handleTranslate();
  });

  if (type === 'mini') {
    return (
      <MiniContainer>
        <MiniHeader>
          <strong>{t('scene.orderWorkspace.returnRefundTab.detail.section.returnRecord')}</strong>
          {translateBtn}
        </MiniHeader>
        <ReturnRecordContent
          showTransalte={translateToggle}
          translateResult={translateResult}
          {...props}
        />
      </MiniContainer>
    );
  } else {
    return (
      <>
        <PanelHeader>
          <PanelTitle>
            {t('scene.orderWorkspace.returnRefundTab.detail.section.returnRecord')}
          </PanelTitle>
          {translateBtn}
        </PanelHeader>
        <PanelContent>
          <ReturnRecordContent
            showTransalte={translateToggle}
            translateResult={translateResult}
            {...props}
          />
        </PanelContent>
      </>
    );
  }
};

const PanelTitle = styled.h3`
  position: relative;
  font-size: 13px;
  padding: 0 18px;
  font-weight: 600;
  margin-bottom: 0;

  &::before {
    content: '';
    position: absolute;
    height: 100%;
    top: 50%;
    left: 0;
    border-left: 4px solid #3370ff;
    transform: translateY(-50%);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelContent = styled.div`
  padding: 8px;
  margin: 16px;
  border: 1px solid var(--fett-border-default);
`;

const MiniContainer = styled.div`
  border: 1px solid var(--fett-border-default);
  margin-bottom: 8px;
`;

const MiniHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--fett-erp-card-bg);
  padding: 10px 16px;
  border-bottom: 1px solid rgba(234, 236, 237, 1);
`;

const CollapsePanel = styled(Collapse.Panel)`
  .ant-collapse-content {
    margin-left: 56px;
  }
  &.ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
  }
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid var(--fett-app-primary-color);
`;

const Time = styled.div`
  color: var(--fett-text-tertiary);
  margin-left: 16px;
`;

const Label = styled.div`
  color: var(--fett-text-secondary);
  margin-bottom: 2px;
`;
